import React from "react";

import FormFieldImage from "./FormFieldImage";

/**
 * Display-only "info screen" field: a headline, body text, and an optional
 * image, with no input. Slide-show forms use these as their welcome,
 * transition, and closing screens.
 *
 * Shared by every surface that renders a form so the screen reads the same in
 * the player (variant "slide"), a scrolling form or preview (variant "inline"),
 * and the editor card. Text arrives already piped — callers resolve
 * {{field:…}} tokens against whatever answers they have (the respondent's, the
 * snapshot's, or none).
 */
export default function FormInfoScreen({
  label,
  help,
  imageUrl,
  variant = "inline",
  className = "",
}) {
  const isSlide = variant === "slide";
  const hasText = !!(label || help);
  // An empty screen (no text, no image) still needs to occupy the slide, or the
  // player would render a blank frame with no hint of what it is.
  if (!hasText && !imageUrl) {
    return (
      <p className={`text-sm text-slate-400 ${className}`}>
        This screen has no content yet.
      </p>
    );
  }

  return (
    <div className={className}>
      <FormFieldImage
        url={imageUrl}
        size={isSlide ? "slide" : "inline"}
        className={isSlide ? "mb-6" : "mb-3"}
      />
      {label && (
        <h2
          className={
            isSlide
              ? "text-3xl font-bold text-slate-900 sm:text-2xl"
              : "text-base font-semibold text-slate-900"
          }
        >
          {label}
        </h2>
      )}
      {help && (
        <p
          className={[
            "whitespace-pre-wrap text-slate-600",
            isSlide ? "mt-3 text-base" : "mt-1.5 text-sm",
            label ? "" : "mt-0",
          ].join(" ")}
        >
          {help}
        </p>
      )}
    </div>
  );
}
